import { Switch } from "@clockwise/design-system";
import { Info, OpenInNew, SvgIconComponent } from "@clockwise/design-system/icons";
import { fg_brand } from "@clockwise/design-system/tokens";
import { InfoFilled } from "@clockwise/icons";
import { AutopilotEventStatusEnum as FlexibilityEnum } from "@clockwise/schema";
import { Sticker } from "@clockwise/web-commons/src/components/sticker/Sticker";
import { Tooltip } from "@clockwise/web-commons/src/ui/tooltip";
import { getUrlForFeature } from "@clockwise/web-commons/src/util/routes.util";
import classNames from "classnames";
import { noop } from "lodash";
import React from "react";
import { useMonetization } from "../../../hooks/useMonetization";
import { Async } from "../atoms/Async";
import { EventElementsWrap } from "../atoms/EventElementsWrap";
import { InlineAction } from "../atoms/InlineAction";

export type Flexibility = FlexibilityEnum | "Stuck" | "Default";

export const isFlexibilityDisabled = (flexibility: Flexibility) => {
  switch (flexibility) {
    case FlexibilityEnum.External:
    case FlexibilityEnum.OverQuota:
    case FlexibilityEnum.TrialExpired:
    case FlexibilityEnum.NoDuration:
    case FlexibilityEnum.PrivateEvent:
      return true;
    default:
      return false;
  }
};

const ViewFlexSettings = ({ onClick }: { onClick: () => void }) => {
  return (
    <>
      {" "}
      <InlineAction onClick={onClick} ariaLabel="Edit Flexibility Settings" label="Edit Settings" />
    </>
  );
};

export const ECFlexibility = ({
  flexibilityDescription,
  flexibilityStatus,
  isFlexible,
  onFlexibilityChange = noop,
  onPauseChange = noop,
  goToFlexDetails = noop,
  readOnly = false,
  loading = false,
  showChanges = false,
}: {
  flexibilityDescription: string;
  flexibilityStatus: Flexibility;
  isFlexible: boolean;
  goToFlexDetails?: () => void;
  onFlexibilityChange?: (flexible: boolean) => void;
  onPauseChange?: (paused: boolean) => void;
  readOnly?: boolean;
  loading?: boolean;
  showChanges?: boolean;
}) => {
  const isDisabled = readOnly || isFlexibilityDisabled(flexibilityStatus);

  const userCanMoveButIsOff = !isDisabled && !isFlexible;

  return (
    <EventElementsWrap showChanges={showChanges} name="flex">
      <Async loading={loading} loadingHeight="28px" containerClasses="cw-mr-2">
        <div
          className={classNames("cw-body-base cw-flex cw-justify-between cw-flex-1", {
            "cw-items-start": !userCanMoveButIsOff,
            "cw-items-center": userCanMoveButIsOff,
          })}
        >
          <div>
            <div className="cw-flex-1 cw-flex cw-space-x-1 cw-mb-1 cw-body-sm cw-leading-4 cw-h-5 cw-items-center">
              <FlexibilityStatus flexible={isFlexible} flexibility={flexibilityStatus} />
            </div>
            {userCanMoveButIsOff || readOnly ? null : (
              <FlexibilityDescription
                flexibility={flexibilityStatus}
                goToFlexDetails={goToFlexDetails}
                disabled={isDisabled}
                flexibilityDescription={flexibilityDescription}
                onPauseChange={onPauseChange}
              />
            )}
          </div>
          <FlexibilityControl
            disabled={isDisabled}
            flexible={isFlexible}
            flexibility={flexibilityStatus}
            onFlexibilityChange={onFlexibilityChange}
          />
        </div>
      </Async>
    </EventElementsWrap>
  );
};

const FlexibilityStatus = ({
  flexible,
  flexibility,
}: {
  flexible: boolean;
  flexibility: Flexibility;
}) => {
  const { userIsOnActiveTrial } = useMonetization();
  const text = flexible ? "Flexible meeting" : "Mark as flexible";

  let stickerText = "";
  let stickerSentiment: "neutral" | "warning" | "info" = "neutral";
  let tooltipText =
    "Flexible meetings indicate that the meeting can move to the best time for you and attendees within the preferences you've set.";
  let stickerIcon: SvgIconComponent | undefined = undefined;

  const userIsOnTrialAndCanMove = userIsOnActiveTrial && flexibility === FlexibilityEnum.CanMove;

  switch (flexibility) {
    case FlexibilityEnum.Pinned:
      if (flexible) {
        // We only want the badge to show up when a meeting is marked flex
        stickerText = "Will not move";
        tooltipText =
          "Clockwise will not change events on your calendar that occur in the next 24 hours, or events that have already taken place. Future meetings in this series will still move.";
        stickerIcon = Info;
      }
      break;
    case FlexibilityEnum.Paused:
      stickerText = "Paused";
      stickerIcon = Info;
      tooltipText =
        "This meeting is paused because it was manually rescheduled. Clockwise will not move a flexible meeting that has been manually rescheduled.";
      break;
    case "Stuck":
      if (flexible) {
        // We only want the badge to show up when a meeting is marked flex
        stickerText = "Stuck";
        stickerSentiment = "warning";
        tooltipText =
          "Clockwise can't find any overlap in attendees' meeting hours. This meeting may need to be manually rescheduled.";
        stickerIcon = Info;
      }
      break;
    case FlexibilityEnum.TrialExpired:
    case FlexibilityEnum.OverQuota:
      stickerText = "TEAMS";
      stickerSentiment = "info";
      break;
  }
  if (userIsOnTrialAndCanMove) {
    stickerText = "TEAMS";
    stickerSentiment = "info";
  }

  return (
    <>
      <div
        className={classNames({
          "cw-text-default cw-font-medium": flexible,
          "cw-text-muted cw-font-medium": !flexible,
        })}
      >
        {text}
      </div>
      <Tooltip showArrow={false} placement="top" body={tooltipText}>
        {!stickerText ? (
          <InfoFilled className="cw-w-4 cw-h-4 cw-mb-[-4px] cw-cursor-pointer cw-text-info-disabled" />
        ) : (
          <Sticker sentiment={stickerSentiment} Icon={stickerIcon} text={stickerText} />
        )}
      </Tooltip>
    </>
  );
};

const capitalizeFirstLetter = (txt: string) => {
  return txt.charAt(0).toUpperCase() + txt.slice(1);
};

const onClickUpgradeRoute = () => {
  const updatePlanUrl = getUrlForFeature("plansAndBilling", { relative: true });
  window.open(updatePlanUrl, "_blank");
};

const FlexibilityDescription = ({
  flexibility,
  flexibilityDescription,
  onPauseChange,
  disabled,
  goToFlexDetails,
}: {
  flexibility: Flexibility;
  flexibilityDescription: string;
  onPauseChange?: (paused: boolean) => void;
  disabled: boolean;
  goToFlexDetails: () => void;
}) => {
  let Description: JSX.Element | null = null;

  switch (flexibility) {
    case FlexibilityEnum.CanMove:
      Description = (
        <>
          {capitalizeFirstLetter(flexibilityDescription) + "."}
          {!disabled && <ViewFlexSettings onClick={goToFlexDetails} />}
        </>
      );
      break;
    case FlexibilityEnum.OverQuota:
    case FlexibilityEnum.TrialExpired:
      Description = (
        <>
          Flexible meeting moves aren't supported on the Free plan.{" "}
          <InlineAction
            onClick={onClickUpgradeRoute}
            ariaLabel="Upgrade Plan"
            label="Upgrade"
            icon={
              <OpenInNew
                style={{
                  fontSize: "13px",
                  color: fg_brand,
                  bottom: "-3px",
                  position: "relative",
                  marginBottom: "1px",
                }}
              />
            }
          />{" "}
          to let your flexible meetings move again.
        </>
      );
      break;
    case FlexibilityEnum.Paused:
      Description = (
        <>
          Meeting was manually rescheduled and won't move unless unpaused. Future meetings in this
          series will still move.{" "}
          <InlineAction
            onClick={() => {
              onPauseChange?.(false);
            }}
            ariaLabel="Unpause Event"
            label="Unpause"
          />{" "}
          {!disabled && (
            <>
              {" ⸱"}
              <ViewFlexSettings onClick={goToFlexDetails} />
            </>
          )}
        </>
      );
      break;
    case FlexibilityEnum.Pinned:
      Description = (
        <>
          Clockwise won't move meetings within 20 hours or in the past. Future meetings in this
          series will still move.{!disabled && <ViewFlexSettings onClick={goToFlexDetails} />}
        </>
      );
      break;
    case FlexibilityEnum.EventExceedsDefragRange:
      Description = (
        <>
          Event exceeds defrag range.{!disabled && <ViewFlexSettings onClick={goToFlexDetails} />}
        </>
      );
      break;
    case FlexibilityEnum.EventOutsideTimeRange:
      Description = (
        <>
          Event outside flexible hours.{!disabled && <ViewFlexSettings onClick={goToFlexDetails} />}
        </>
      );
      break;
    case FlexibilityEnum.External:
      Description = <>Meetings with external attendees can't be marked as flexible.</>;
      break;
    case FlexibilityEnum.NoDuration:
      Description = <>Event has no duration.</>;
      break;
    case FlexibilityEnum.PrivateEvent:
      Description = <>Event is private.</>;
      break;
    case FlexibilityEnum.SmartHold:
      Description = <>Event is smart hold.</>;
      break;
    case "Default":
      Description = <>{!disabled && <ViewFlexSettings onClick={goToFlexDetails} />}</>;
      break;
    case "Stuck":
      Description = (
        <>
          Meeting cannot move based on current settings.
          {!disabled && <ViewFlexSettings onClick={goToFlexDetails} />}
        </>
      );
      break;
  }

  if (Description) {
    return (
      <div className="cw-body-sm cw-text-12 cw-leading-[18px] cw-text-muted cw-pr-3">
        {Description}
      </div>
    );
  }

  return null;
};

const FlexibilityControl = ({
  disabled,
  flexible,
  flexibility,
  onFlexibilityChange,
}: {
  disabled: boolean;
  flexible: boolean;
  flexibility: Flexibility;
  onFlexibilityChange: (flexible: boolean) => void;
}) => {
  const dontShowToggle =
    flexibility === FlexibilityEnum.Paused ||
    flexibility === FlexibilityEnum.TrialExpired ||
    flexibility === FlexibilityEnum.OverQuota;

  if (!dontShowToggle) {
    return (
      <FlexibilitySwitch flexible={flexible} disabled={disabled} onChange={onFlexibilityChange} />
    );
  } else {
    return null;
  }
};

const FlexibilitySwitch = ({
  flexible,
  disabled,
  onChange,
}: {
  disabled: boolean;
  flexible: boolean;
  onChange: (flexible: boolean) => void;
}) => {
  return (
    <div>
      <Switch
        size="small"
        checked={flexible}
        disabled={disabled}
        onChange={onChange}
        aria-label="Toggle flexibility"
      />
    </div>
  );
};
